import { useEffect, useRef } from "react";
import { pendingAuthPaymentConfigStore } from "../../../hookstate/pendingAuthPaymentConfigStore";
import type { SessionPayloadStore } from "../utils/sessionPayloadStore";
import { useLatestRef } from "../../../hooks/useLatestRef";

/**
 * After auth completes, attempt to resume a pending guest purchase that was
 * captured before signup/login. Reads Hookstate first (preferred), falls back
 * to the sessionStorage payload store. Latches once per mount so the effect
 * doesn't re-fire when its dep changes mid-flight.
 *
 * Loading state is intentionally NOT exposed: the caller's `usePaymentCompletion`
 * already exposes `isLoading` for the same call (since `onResume` delegates to
 * its `run`).
 */
export function usePendingAuthPurchaseResume<T>(opts: {
  /** Gates the effect — typically `!!userData && !!entityKey`. */
  enabled: boolean;
  /** Re-runs the effect when the entity in scope changes. */
  entityKey: string | number | undefined;
  payloadStore: SessionPayloadStore<T>;
  /** Returns true if the loaded payload corresponds to the entity in scope. */
  payloadMatchesEntity: (payload: T) => boolean;
  /** The actual purchase completion call. Hook handles its catch internally. */
  onResume: (payload: T) => Promise<unknown>;
}): void {
  const { enabled, entityKey, payloadStore } = opts;
  const handledRef = useRef(false);
  const payloadMatchesEntityRef = useLatestRef(opts.payloadMatchesEntity);
  const onResumeRef = useLatestRef(opts.onResume);

  useEffect(() => {
    if (handledRef.current || !enabled || entityKey === undefined) return;

    const stored = pendingAuthPaymentConfigStore.get({ noproxy: true });
    // Hookstate is the cross-funnel hand-off; sessionStorage is the cross-mount
    // fallback. Validate Hookstate against the same predicate the store uses so
    // a Bundle modal never tries to resume a Release payload (or vice versa).
    const payload: T | undefined = payloadStore.isValid(stored)
      ? stored
      : payloadStore.read();
    if (!payload || !payloadMatchesEntityRef.current(payload)) return;

    handledRef.current = true;
    void onResumeRef.current(payload).catch(() => {
      handledRef.current = false;
    });
  }, [enabled, entityKey, payloadStore, payloadMatchesEntityRef, onResumeRef]);
}
